import React from "react";
import NcModal from "designs/Common/NcModal/NcModal";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import CelebrationIcon from "images/designB/celebrationicon.png";
import moment from "moment";
import { formatPrice } from "utils/convertNumbThousand";
import toast from "react-hot-toast";

interface BookingSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  bookingId?: string | number;
  uniqueIdentifier?: string;
  pickUpDate?: Date | null;
  pickUpTime?: string;
  dropOffDate?: Date | null;
  dropOffTime?: string;
  totalPrice?: string;
  ctaLabel?: string;
}

const BookingSuccessModal: React.FC<BookingSuccessModalProps> = ({
  isOpen,
  onClose,
  title = "Booking Confirmed!",
  message = "Your booking has been successfully processed.",
  bookingId,
  uniqueIdentifier,
  pickUpDate,
  pickUpTime,
  dropOffDate,
  dropOffTime,
  totalPrice,
  ctaLabel = "Done",
}) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(uniqueIdentifier || String(bookingId) || "");
    toast.success("Booking ID copied to clipboard");
  };

  return (
    <NcModal
      isOpenProp={isOpen}
      onCloseModal={onClose}
      modalTitle=""
      contentExtraClass="max-w-lg"
      renderContent={() => (
        <div className="flex flex-col items-center text-center">
          <img src={CelebrationIcon} alt="Success" className="mb-4 w-24" />
          <h2 className="text-2xl font-bold text-[#18181B] mb-2">{title}</h2>
          <p className="text-[#71717A] mb-6 text-sm">{message}</p>

          <div className="w-full bg-[#12121208] rounded-xl p-4 mb-6 space-y-3 text-left">
            {bookingId && (
              <div className="flex justify-between items-center">
                <span className="text-[#71717A] text-sm">Booking ID</span>
                <div className="flex items-center gap-2">
                  <span className="text-[#18181B] font-semibold">
                    {bookingId}
                  </span>
                  <button
                    onClick={handleCopy}
                    className="text-gray-400 hover:text-[#71717A] transition-colors"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {pickUpDate && (
              <div className="flex justify-between items-center">
                <span className="text-[#71717A] text-sm">Pick-up</span>
                <span className="text-[#18181B] font-semibold text-right text-sm">
                  {moment(pickUpDate).format("ddd, D MMM YYYY")}
                  {pickUpTime ? ` ${pickUpTime}` : ""}
                </span>
              </div>
            )}

            {dropOffDate && (
              <div className="flex justify-between items-center">
                <span className="text-[#71717A] text-sm">Drop-off</span>
                <span className="text-[#18181B] font-semibold text-right text-sm">
                  {moment(dropOffDate).format("ddd, D MMM YYYY")}
                  {dropOffTime ? ` ${dropOffTime}` : ""}
                </span>
              </div>
            )}

            {totalPrice && (
              <div className="flex justify-between items-center">
                <span className="text-[#71717A] text-sm">Total</span>
                <span className="text-[#18181B] font-bold text-lg">
                  ${formatPrice(totalPrice)}
                </span>
              </div>
            )}
          </div>

          <ButtonPrimary onClick={onClose} className="w-full rounded-xl">
            {ctaLabel}
          </ButtonPrimary>
        </div>
      )}
    />
  );
};

export default BookingSuccessModal;
